import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowRight,
  Building2,
  RefreshCw,
  MoreVertical,
  Edit,
  Power,
  Package,
  Copy,
  Check,
  Wallet,
  FileText,
  TrendingUp,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { TenantDetails as TenantDetailsType } from '@/types/tenant.types';
import {
  TenantInfoCard,
  TenantDeliverySettings,
  TenantShipmentsList,
  TenantSettlements,
  TenantWarehousesList,
  TenantConnectionCard,
  TenantFCAssignment,
} from '@/components/features/tenants/details';
import { cn } from '@/lib/utils';
import { useI18n } from '@/contexts/I18nContext';

export default function TenantDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tp } = useI18n();
  const { get, loading } = useApi();
  
  const [tenant, setTenant] = useState<TenantDetailsType | null>(null);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [stats, setStats] = useState<{ total: number; pending: number; assigned: number; delivered: number }>({ total: 0, pending: 0, assigned: 0, delivered: 0 });

  const fetchTenantDetails = async () => {
    setHasFetched(false);

    try {
      const result: any = await get(`/api/v1/tenants/${id}`);
      const tenantData = result?.success !== undefined ? result?.data : result;

      if (tenantData) {
        setTenant(tenantData);
      } else {
        setTenant(null);
      }
    } catch (error) {
      console.error('Failed to fetch tenant details:', error);
      toast.error(tp.tenants.loadError);
      setTenant(null);
    } finally {
      setHasFetched(true);
    }
  };

  const fetchStats = async () => {
    try {
      const result: any = await get(`/api/v1/dashboard/tenants/${id}/stats`);
      const data = result?.data || result;
      if (data) {
        setStats({
          total: data.total || 0,
          pending: data.pending || 0,
          assigned: data.assigned || 0,
          delivered: data.delivered || 0,
        });
      }
    } catch (error) {
      console.error('Failed to fetch tenant stats:', error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchTenantDetails();
      fetchStats();
    }
  }, [id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchTenantDetails(), fetchStats()]);
    setRefreshing(false);
    toast.success(tp.commonExt.refreshSuccess);
  };

  const handleCopyId = async () => {
    if (id) {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      toast.success(tp.tenants.copiedSuccess);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleBack = () => {
    navigate('/dashboard/tenants');
  };

  if ((!hasFetched || loading) && !tenant) {
    return (
      <PageContainer>
        <div className="space-y-6">
          <LoadingSkeleton type="card" rows={1} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <LoadingSkeleton type="card" rows={2} />
            <LoadingSkeleton type="card" rows={2} />
          </div>
        </div>
      </PageContainer>
    );
  }

  if (hasFetched && !tenant) {
    return (
      <PageContainer>
        <EmptyState
          icon={<Building2 className="h-16 w-16" />}
          title={tp.tenants.notFound}
          description={tp.tenants.notFoundDescription}
          action={{
            label: tp.tenants.backToList,
            onClick: handleBack,
          }}
        />
      </PageContainer>
    );
  }

  const tenantName = tenant.name_translations?.ar || tenant.name_translations?.en || tenant.subdomain;

  return (
    <PageContainer>
      {/* Header Section */}
      <div className="mb-6 space-y-4" dir="rtl">
        {/* Top Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-9 w-9"
            >
              <ArrowRight className="h-5 w-5" />
            </Button>
            
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl md:text-2xl font-bold">{tenantName}</h1>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-xs",
                      tenant.is_active
                        ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800"
                        : "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800"
                    )}
                  >
                    {tenant.is_active ? tp.commonExt.active : tp.commonExt.inactive}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{tenant.subdomain}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 me-12 sm:me-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyId}
              className="h-9"
            >
              {copied ? (
                <Check className="h-4 w-4 ms-2 text-green-500" />
              ) : (
                <Copy className="h-4 w-4 ms-2" />
              )}
              {tp.commonExt.copyId}
            </Button>
            
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing}
              className="h-9 w-9"
            >
              <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem>
                  <Edit className="h-4 w-4 ms-2" />
                  {tp.commonExt.editData}
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Power className="h-4 w-4 ms-2" />
                  {tenant.is_active ? tp.tenants.disableTenant : tp.tenants.enableTenant}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Quick Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <QuickStatCard
            label={tp.tenants.totalShipments}
            value={stats.total}
            icon={<Package className="h-4 w-4" />}
          />
          <QuickStatCard
            label={tp.tenants.newShipments}
            value={stats.pending}
            icon={<TrendingUp className="h-4 w-4" />}
            highlight="blue"
          />
          <QuickStatCard
            label={tp.tenants.inDelivery}
            value={stats.assigned}
            icon={<Clock className="h-4 w-4" />}
            highlight="orange"
          />
          <QuickStatCard
            label={tp.tenants.completedShipments}
            value={stats.delivered}
            icon={<CheckCircle2 className="h-4 w-4" />}
            highlight="green"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4" dir="rtl">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" className="text-sm">
            {tp.tenants.overview}
          </TabsTrigger>
          <TabsTrigger value="shipments" className="text-sm">
            {tp.tenants.shipments}
          </TabsTrigger>
          <TabsTrigger value="warehouses" className="text-sm">
            {tp.tenants.warehouses}
          </TabsTrigger>
          <TabsTrigger value="fc" className="text-sm">
            {tp.tenants.fulfillmentCenter}
          </TabsTrigger>
          <TabsTrigger value="settlements" className="text-sm">
            {tp.tenants.settlements}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TenantInfoCard tenant={tenant} />
            <TenantConnectionCard tenantId={id!} />
          </div>
          <TenantDeliverySettings tenant={tenant} />
        </TabsContent>

        <TabsContent value="shipments">
          <TenantShipmentsList tenantId={id!} />
        </TabsContent>

        <TabsContent value="warehouses">
          <TenantWarehousesList tenantId={id!} />
        </TabsContent>

        <TabsContent value="fc">
          <TenantFCAssignment tenantId={id!} />
        </TabsContent>

        <TabsContent value="settlements">
          <TenantSettlements tenantId={id!} />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}

// Quick Stat Card Component
interface QuickStatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  highlight?: 'blue' | 'orange' | 'green';
}

function QuickStatCard({ label, value, icon, highlight }: QuickStatCardProps) {
  const highlightClasses = {
    blue: 'border-blue-500/50 bg-blue-500/5 text-blue-600',
    orange: 'border-orange-500/50 bg-orange-500/5 text-orange-600',
    green: 'border-green-500/50 bg-green-500/5 text-green-600',
  };

  return (
    <Card className={cn("transition-colors", highlight && highlightClasses[highlight])}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className={cn("text-muted-foreground", highlight && highlightClasses[highlight])}>
            {icon}
          </span>
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <div className="flex items-baseline gap-1">
          <span className={cn("text-lg font-bold", highlight && highlightClasses[highlight])}>
            {value}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
